import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { 
 CreditCard, RotateCw, CheckCircle2, Gift, Power, 
 Loader2, Trophy, Save, Users, PlusCircle, MinusCircle, Search, Ticket,
 PackageCheck, History
} from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/Fidelidade.css'; 

const Fidelidade = ({ estabelecimentoId }) => {
 const [flipped, setFlipped] = useState(false);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [busca, setBusca] = useState('');
 const [clientes, setClientes] = useState([]); 
 const [produtos, setProdutos] = useState([]);
  
 const [config, setConfig] = useState({ 
  ativo: false, 
  qtd_objetivo: 10, 
  premio: '', 
  produto_id: '', 
  cor_cartao: '#1e293b' 
 });

 const carregarDados = useCallback(async () => {
  try {
   setLoading(true);
   
   const resProdutos = await api.get(`/cardapio/produtos/simples/${estabelecimentoId}`).catch(() => ({ data: [] }));
   setProdutos(resProdutos.data || []);

   const resConfig = await api.get(`/marketing/fidelidade/${estabelecimentoId}`);
   if (resConfig.data) {
    setConfig({ 
     ativo: resConfig.data.ativo === 1,
     qtd_objetivo: resConfig.data.qtd_objetivo || 10,
     premio: resConfig.data.premio || '',
     produto_id: resConfig.data.produto_id || '',
     cor_cartao: resConfig.data.cor_cartao || '#1e293b'
    });
   }

   const resRanking = await api.get(`/marketing/fidelidade/ranking/${estabelecimentoId}`);
   setClientes(resRanking.data || []);

  } catch (err) { 
   console.error("Erro ao carregar dados:", err);
  } finally {
   setLoading(false);
  }
 }, [estabelecimentoId]);

 useEffect(() => {
  carregarDados();
 }, [carregarDados]);

 const handleProdutoChange = (e) => {
  const selectedId = e.target.value;
  const produtoSelected = produtos.find(p => p.id === parseInt(selectedId));
  setConfig({
   ...config,
   produto_id: selectedId,
   premio: produtoSelected ? produtoSelected.nome : ''
  });
 };

 const handleSalvar = async () => {
  if (!config.produto_id) return toast.error("Selecione um produto como prêmio!");
  setSaving(true);
  try {
   await api.post('/marketing/fidelidade/config', { 
    ...config, 
    estabelecimento_id: estabelecimentoId,
    ativo: config.ativo ? 1 : 0
   });
   toast.success("Configuração Salva!");
   carregarDados();
  } catch (err) { 
   toast.error("Erro ao salvar regras"); 
  } finally {
   setSaving(false);
  }
 };

 const toggleStatus = async () => {
  try {
   const novoStatus = !config.ativo;
   await api.put(`/marketing/fidelidade/${estabelecimentoId}/status`, { ativo: novoStatus ? 1 : 0 });
   setConfig({ ...config, ativo: novoStatus });
   toast.success(novoStatus ? "Programa Ativado!" : "Programa Pausado");
  } catch (err) { 
   toast.error("Erro ao alterar status"); 
  }
 };

 const ajustarSeloManual = async (clienteId, operacao) => {
  try {
   await api.put('/marketing/fidelidade/manual', {
    cliente_id: clienteId,
    estabelecimento_id: estabelecimentoId,
    operacao
   });
   toast.success(operacao === 'add' ? "Selo Adicionado!" : "Selo Removido");
   carregarDados(); 
  } catch (err) {
   toast.error("Erro ao atualizar selos.");
  }
 };

 const clientesFiltrados = clientes.filter(c => 
  c.nome?.toLowerCase().includes(busca.toLowerCase()) || 
  c.whatsapp?.includes(busca)
 );

 if (loading) return (
  <div style={{ display: 'flex', flexDirection:'column', alignItems: 'center', justifyContent: 'center', padding: '100px', gap: '20px' }}>
   <Loader2 className="animate-spin" size={40} color="#1e293b" />
   <span style={{ fontWeight: 'bold', color: '#64748b' }}>Sincronizando Cartões...</span>
  </div>
 );

 return (
  <div className="marketing-container" style={{ padding: '20px', background: '#f8fafc', minHeight: '100vh' }}>
    
   <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
    <div>
     <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontWeight: 900, color: '#1e293b', fontSize: '24px', margin: 0 }}>
      <div style={{ background: '#fff', padding: '8px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
       <Trophy size={28} color="#f59e0b" />
      </div>
      Fidelidade Digital
     </h2>
    </div>
    <button 
     onClick={toggleStatus}
     style={{ 
      background: config.ativo ? '#22c55e' : '#94a3b8',
      color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '12px',
      display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '900', fontSize: '14px'
     }}
    >
     <Power size={18} /> {config.ativo ? 'ATIVO' : 'DESATIVADO'}
    </button>
   </header>

   <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '30px' }}>
     
    {/* CARTÃO PREVIEW PREMIUM */}
    <div className="fidelidade-card-scene" onClick={() => setFlipped(!flipped)}>
      <div className={`fidelidade-card-inner ${flipped ? 'flipped' : ''}`}>
        
        {/* FRENTE */}
        <div className="fidelidade-face fidelidade-front" style={{ background: config.cor_cartao }}>
          <div className="fidelidade-glow"></div>
          <div className="fidelidade-lines"></div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
            <div className="fidelidade-chip-premium">
              <div className="chip-line"></div>
              <div className="chip-line"></div>
              <div className="chip-line"></div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Trophy size={22} color="#fff" style={{ opacity: 0.9 }} />
              <div style={{ fontSize: '8px', fontWeight: 'bold', letterSpacing: '1px', opacity: 0.6, marginTop: '2px' }}>LOYALTY CARD</div>
            </div>
          </div>

          <div style={{ marginTop: '25px', position: 'relative', zIndex: 2 }}>
            <div className="fidelidade-label" style={{ color: 'rgba(255,255,255,0.6)' }}>O cliente ganha:</div>
            <h3 className="fidelidade-premio-txt" style={{ fontSize: '20px', letterSpacing: '-0.5px' }}>
              {config.premio || 'Escolha um produto...'}
            </h3>
          </div>

          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative', zIndex: 2 }}>
            <div>
              <div className="fidelidade-label" style={{ color: 'rgba(255,255,255,0.6)' }}>META DO CARTÃO</div>
              <div style={{ fontWeight: '900', fontSize: '16px', color: '#fff' }}>{config.qtd_objetivo} SELOS</div>
            </div>
            <RotateCw size={18} color="#fff" style={{ opacity: 0.5 }} />
          </div>
        </div>

        {/* VERSO */}
        <div className="fidelidade-face fidelidade-back">
          <div style={{ textAlign: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px', marginBottom: '10px' }}>
            <span className="fidelidade-label" style={{ color: '#94a3b8', fontWeight: '900' }}>Grade de Carimbos</span>
          </div>
          <div className="fidelidade-grid-carimbos">
            {Array.from({ length: config.qtd_objetivo }).map((_, i) => (
              <div key={i} className="fidelidade-slot-premium">
                <div className="slot-inner"></div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 'auto', textAlign: 'center' }}>
            <div style={{ fontSize: '9px', color: '#cbd5e1', fontWeight: 'bold' }}>UNIVERSO MÍDIA DIGITAL</div>
          </div>
        </div>

      </div>
    </div>

    {/* PAINEL DE CONFIGURAÇÃO */}
    <div style={{ background: '#fff', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '15px' }}>
     <h3 style={{ margin: 0, fontWeight: 900, color: '#1e293b', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
       <PackageCheck size={20} color={config.cor_cartao} /> Configurações Gerais
     </h3>
     
     <div>
      <label style={{ display: 'block', marginBottom: '6px', fontSize: '11px', fontWeight: '900', color: '#94a3b8' }}>PRODUTO DA RECOMPENSA</label>
      <select 
       value={config.produto_id}
       onChange={handleProdutoChange}
       className="input-fidelidade"
      >
       <option value="">Selecione um produto...</option>
       {produtos.map(p => (
        <option key={p.id} value={p.id}>{p.nome}</option>
       ))}
      </select>
     </div>

     <div style={{ display: 'flex', gap: '15px' }}>
      <div style={{ flex: 1 }}>
       <label style={{ display: 'block', marginBottom: '6px', fontSize: '11px', fontWeight: '900', color: '#94a3b8' }}>QTD. SELOS</label>
       <select 
        value={config.qtd_objetivo} 
        onChange={(e) => setConfig({...config, qtd_objetivo: parseInt(e.target.value)})}
        className="input-fidelidade"
       >
        {[5, 10, 15].map(n => <option key={n} value={n}>{n} Selos</option>)}
       </select>
      </div>
      <div style={{ flex: 1 }}>
       <label style={{ display: 'block', marginBottom: '6px', fontSize: '11px', fontWeight: '900', color: '#94a3b8' }}>COR DO CARD</label>
       <input 
         type="color" 
         value={config.cor_cartao} 
         onChange={(e) => setConfig({...config, cor_cartao: e.target.value})} 
         className="input-color-fidelidade"
       />
      </div>
     </div>

     <button 
      onClick={handleSalvar} 
      disabled={saving}
      className="btn-salvar-fidelidade"
      style={{ background: config.cor_cartao }}
     >
      {saving ? <Loader2 className="animate-spin" size={20}/> : <><Save size={20}/> SALVAR CONFIGURAÇÃO</>}
     </button>
    </div>
   </div>

   {/* TABELA DE GESTÃO */}
   <div style={{ background: '#fff', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
     <h3 style={{ fontWeight: 900, color: '#1e293b', margin: 0, fontSize: '18px' }}>Clientes no Programa</h3>
     <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
      <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
      <input 
       type="text" 
       placeholder="Buscar por nome ou whats..." 
       className="input-fidelidade"
       style={{ paddingLeft: '40px' }}
       value={busca}
       onChange={(e) => setBusca(e.target.value)}
      />
     </div>
    </div>

    <div style={{ overflowX: 'auto' }}>
     <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
      <thead>
       <tr style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'left', fontWeight: '900', textTransform: 'uppercase' }}>
        <th style={{ padding: '0 15px' }}>Cliente</th>
        <th>Progresso Atual</th>
        <th style={{ textAlign: 'center' }}>Situação</th>
        <th style={{ textAlign: 'center' }}>Carimbar</th>
       </tr>
      </thead>
      <tbody>
       {clientesFiltrados.map((c) => {
        const progressoPercent = Math.min((c.carimbos_atuais / config.qtd_objetivo) * 100, 100);
        const isDisponivel = c.status === 'disponivel';
        const isUtilizado = c.status === 'utilizado';

        return (
         <tr key={c.id} style={{ background: isUtilizado ? '#fcfcfc' : '#fff', opacity: isUtilizado ? 0.7 : 1 }}>
          <td style={{ padding: '12px 15px', borderRadius: '12px 0 0 12px', border: '1px solid #f1f5f9', borderRight: 'none' }}>
           <div style={{ fontWeight: '800', color: '#1e293b', fontSize: '14px' }}>{c.nome}</div>
           <div style={{ fontSize: '12px', color: '#64748b' }}>{c.whatsapp}</div>
          </td>
          <td style={{ padding: '12px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
           <div style={{ width: '100%', minWidth: '120px', height: '8px', background: '#f1f5f9', borderRadius: '10px', overflow: 'hidden', marginBottom: '6px' }}>
            <div style={{ width: `${progressoPercent}%`, height: '100%', background: isDisponivel ? '#22c55e' : config.cor_cartao, transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}></div>
           </div>
           <span style={{ fontSize: '11px', fontWeight: '900', color: '#64748b' }}>{c.carimbos_atuais} de {config.qtd_objetivo} selos</span>
          </td>
          <td style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
            {isUtilizado ? (
             <span style={{ background: '#f1f5f9', color: '#94a3b8', padding: '5px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '900' }}>RESGATADO</span>
            ) : isDisponivel ? (
             <span className="animate-pulse" style={{ background: '#dcfce7', color: '#16a34a', padding: '5px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '900', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Trophy size={12}/> LIBERADO
             </span>
            ) : (
             <span style={{ background: '#eff6ff', color: '#3b82f6', padding: '5px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '900' }}>EM ANDAMENTO</span>
            )}
          </td>
          <td style={{ padding: '12px', textAlign: 'center', borderRadius: '0 12px 12px 0', border: '1px solid #f1f5f9', borderLeft: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
             <button 
              onClick={() => ajustarSeloManual(c.cliente_id, 'remove')} 
              disabled={isUtilizado || c.carimbos_atuais === 0}
              className="btn-acao-selo remove"
             >
              <MinusCircle size={20} />
             </button>
             <button 
              onClick={() => ajustarSeloManual(c.cliente_id, 'add')} 
              disabled={isDisponivel || isUtilizado}
              className="btn-acao-selo add"
              style={{ color: isDisponivel || isUtilizado ? '#cbd5e1' : '#22c55e' }}
             >
              <PlusCircle size={20} />
             </button>
            </div>
          </td>
         </tr>
        );
       })}
      </tbody>
     </table>
    </div>
   </div>
  </div>
 );
};

export default Fidelidade;